import React, { useRef, useEffect, useState } from "react";
import gsap from "gsap";
import DeepDiveOpenButton from "./DeepDive/DeepDiveOpenButton";
import useIsMobile from "../hooks/useIsMobile";
import Tooltip from "./Tooltip";
import BackToTop from "./BackToTop";
import { PageContainer, Callout, CalloutCopyEndFrame } from "./Utilities";

const Outro = React.forwardRef(({ playVideo=false }, ref) => {
  const isMobile = useIsMobile();
  const overlayRef = useRef(null);
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    if (isMobile === null) return;

    if (playVideo) {
      if (!videoLoaded) {
        videoRef.current.src = `./vids/endframe_${isMobile ? "mobile" : "desktop"}.mp4`;
        setVideoLoaded(true);
      }

      gsap.killTweensOf(ref.current);
      gsap.killTweensOf(videoRef.current);

      videoRef.current.play();

      gsap.set([".outro-container", overlayRef.current], { opacity: 0 });
      gsap.to([".outro-container"], { opacity: 1, duration: 0.85, stagger: 0.08, ease: "expo.inOut" });
      gsap.set([".titleEl", ".listEl li"], { opacity: 0 });
      gsap.to([".titleEl", overlayRef.current, ".listEl li"], { opacity: 1, duration: 0.85, stagger: 0.08, ease: "expo.inOut" });
      gsap.to(videoRef.current, { opacity: 1, duration: 0.95, ease: "power2.out", delay: 0.4 });
    } else {
      gsap.killTweensOf(ref.current);
      gsap.killTweensOf(videoRef.current);
      gsap.set([".titleEl", ".listEl li", overlayRef.current], { opacity: 0 });
      gsap.to(videoRef.current, { opacity: 0, duration: 1.0, ease: "power2.out", onComplete: () => {
        videoRef.current.pause();
      }});
    }
  }, [playVideo, isMobile, videoLoaded, ref, videoRef]);

  const hoverEffect = "hover:scale-[1.05] transform ease-out duration-200 pointer-events-auto";
  
  return (
      
    <div ref={ref} id="outroScreen" className={`relative overflow-hidden w-full h-fit z-[1000] !block tablet:-mt-16 pb-8`}>
      
      <PageContainer className="absolute outro-video-background items-center opacity-75 md:opacity-100 bottom-[-60px] lg:bottom-[-140px]">
        <video
          ref={videoRef}
          src=""
          className={`video-background top-0 z-1 opacity-0 !absolute`}
          playsInline={true}
          webkit-playsinline="true"
          preload="auto"
          muted="muted"
          loop
          type="video/mp4"
        />
      </PageContainer>

      <PageContainer className="relative items-center outro-container tablet:mt-16">
        <div className="flex flex-col h-fit md:max-w-[70%] text-center m-auto z-10 pb-2">
          <h2 className="titleEl text-[25px] lg:text-[35px] leading-[31px] lg:leading-[46px] text-white font-bold text-center m-auto mobile:mx-4 pt-0 md:pb-8 lg:w-full mobile:pb-4 mobile:flex-grow mobile:mt-[80px]">
            <Tooltip className="!font-bold">MAC lung disease</Tooltip> can cause serious damage. Don’t wait to ask your doctor if antibiotic treatment is right for you.
          </h2>
          <div ref={overlayRef} className="relative w-[95%] lg:w-[464px] ml-auto mr-auto lg:mr-0 mb-7 p-3 border-[0.5px] border-[rgba(143,244,246,0.62)] opacity-0">
            <span className="corner w-[24.875px] h-[24.875px]  translate-y-[-1px] translate-x-[-1px] TL absolute bg-[url('./img/corner.svg')] -top-[4px] -left-[4px]"></span>
            <span className="corner w-[24.875px] h-[24.875px] translate-y-[-2px] TR absolute bg-[url('./img/corner.svg')] -top-[3px] -right-[4px] rotate-90"></span>
            <span className="corner  w-[24.875px] h-[24.875px] translate-y-[2px] BL absolute bg-[url('./img/corner.svg')] -bottom-[3px] -right-[5px] rotate-180"></span>
            <span className="corner w-[24.875px] h-[24.875px] translate-y-[1px] translate-x-[1px] BR absolute bg-[url('./img/corner.svg')] -bottom-[3px] -left-[5.5px] -rotate-90"></span>
            <ul className="listEl shadow-[inset_0_3px_6px_0_rgba(143,244,246,0.5)] border-[solid_1px_#8ff4f6] bg-[rgba(0,23,23,0.75)]">
              <li className="after:block after:content-[''] after:h-[0.5px] after:bg-[rgba(143,244,246,0.62)] after:w-[290px] after:mx-auto">
                <Callout className={`pointer-events-auto mobile:mx-6 lg:mr-0 !rounded-2xl`}>
                  <CalloutCopyEndFrame>Damage is permanent and gets worse with time.</CalloutCopyEndFrame>
                </Callout>
              </li>
              <li className="after:block after:content-[''] after:h-[0.5px] after:bg-[rgba(143,244,246,0.62)] after:w-[290px] after:mx-auto">
                <Callout className={`mobile:mx-6 lg:mr-0 !rounded-2xl`}>
                  <CalloutCopyEndFrame>
                    <Tooltip>MAC</Tooltip> has a long-term impact on your health.
                  </CalloutCopyEndFrame>
                </Callout>
              </li>
              <li>
                <Callout className={`mobile:mx-6 lg:mr-0 !rounded-2xl`}>
                  <CalloutCopyEndFrame>Both the symptoms and the infection need to be managed.</CalloutCopyEndFrame>
                </Callout>
              </li>
            </ul>
          </div>
          <ul className="listEl pb-16 md:pb-2 w-full lg:w-[440px] ml-auto mr-0">
            <li className="flex justify-end mb-8">
              <div className={`${hoverEffect} relative w-full pt-3`}>
                <DeepDiveOpenButton fullWidth={false} href="/diagnosis" className="!relative !bottom-0 !opacity-100 !block !m-auto w-[291.4px] md:w-[320px]">
                  EXPLORE TREATMENT GUIDELINES
                </DeepDiveOpenButton>
              </div>
            </li>
            <li className="flex justify-center">
              <BackToTop />
            </li>
          </ul>
        </div>
      </PageContainer>
    </div>
  );
});

export default Outro;
